import type {
  AmbassadorPromotionRequest,
  BatchInviteRequest,
  PaginatedResponse,
  ReferralConfig,
  ReferralData,
  ReferralHistoryParams,
  ReferralUser,
} from '@/types/referral.types';
import axiosClient from './axiosClient';

import { API_ENDPOINTS } from '@/constants';

/**
 * Referral API Service
 * Handles user referral data, earnings, and admin management.
 */
export const referralApi = {
  getReferralData: async (): Promise<ReferralData> => {
    const { data } = await axiosClient.get<{ success: boolean; data: ReferralData }>(
      API_ENDPOINTS.REFERRAL.STATUS
    );
    return data.data;
  },

  /**
   * Fetch paginated referral history
   */
  getReferralHistory: async (
    params: ReferralHistoryParams = {}
  ): Promise<PaginatedResponse<ReferralUser>> => {
    const { data } = await axiosClient.get<PaginatedResponse<ReferralUser>>(
      API_ENDPOINTS.REFERRAL.HISTORY,
      { params }
    );
    return data;
  },

  /**
   * Validate a referral code and get referrer details
   */
  validateReferralCode: async (code: string) => {
    const { data } = await axiosClient.get<{
      success: boolean;
      data: { isValid: boolean; referrerName: string; tier: string; isAmbassador: boolean };
    }>(API_ENDPOINTS.REFERRAL.VALIDATE(code));
    return data.data;
  },

  /**
   * Apply a referral code to the current user's account
   */
  applyReferralCode: async (code: string): Promise<void> => {
    try {
      const { data } = await axiosClient.post<{ success: boolean; message?: string }>(
        API_ENDPOINTS.REFERRAL.APPLY,
        { referralCode: code }
      );
      if (data && data.success === false) {
        throw new Error(data.message || 'Failed to apply referral code');
      }
    } catch (err) {
      const backendMessage = err?.response?.data?.message;
      throw new Error(backendMessage || err?.message || 'Failed to apply referral code');
    }
  },

  /**
   * ADMIN: Fetch current referral configuration
   */
  getConfig: async (): Promise<ReferralConfig> => {
    const { data } = await axiosClient.get<{ success: boolean; data: ReferralConfig }>(
      API_ENDPOINTS.REFERRAL.CONFIG
    );
    return data.data;
  },

  /**
   * ADMIN: Update global referral configuration
   */
  updateConfig: async (config: ReferralConfig): Promise<void> => {
    try {
      const { data } = await axiosClient.post<{ success: boolean; message?: string }>(
        API_ENDPOINTS.REFERRAL.CONFIG,
        config
      );
      if (data && data.success === false) {
        throw new Error(data.message || 'Failed to update configuration');
      }
    } catch (err: any) {
      const backendMessage = err?.response?.data?.message;
      throw new Error(backendMessage || err?.message || 'Failed to update configuration');
    }
  },

  /**
   * ADMIN: Promote/Demote a user to Ambassador status
   */
  promoteToAmbassador: async (
    userId: string,
    payload: AmbassadorPromotionRequest
  ): Promise<void> => {
    try {
      const { data } = await axiosClient.put<{ success: boolean; message?: string }>(
        API_ENDPOINTS.REFERRAL.PROMOTE(userId),
        payload
      );
      if (data && data.success === false) {
        throw new Error(data.message || 'Failed to update user status');
      }
    } catch (err: any) {
      const backendMessage = err?.response?.data?.message;
      throw new Error(backendMessage || err?.message || 'Failed to update user status');
    }
  },

  /**
   * ADMIN: Batch invite ambassadors via email
   */
  batchInvite: async (payload: BatchInviteRequest): Promise<void> => {
    try {
      const { data } = await axiosClient.post<{ success: boolean; message?: string }>(
        API_ENDPOINTS.REFERRAL.BATCH_INVITE,
        payload
      );
      if (data && data.success === false) {
        throw new Error(data.message || 'Failed to send invites');
      }
    } catch (err: any) {
      const backendMessage = err?.response?.data?.message;
      throw new Error(backendMessage || err?.message || 'Failed to send invites');
    }
  },
};
